// ITER-marketing-pass2 · Performance Review Panel
// One canonical response drives BOTH the on-screen panel and the
// printable Monthly Performance Review layout. Same scope + same
// period always renders identical numbers on screen and in print.
import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { Button } from '../ui/button';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ComposedChart, Area
} from 'recharts';
import {
  TrendingUp, TrendingDown, Minus, Printer, AlertTriangle, CheckCircle2,
  BarChart3, Users as UsersIcon, Eye, Heart, IndianRupee, Layers,
  Wifi
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PERIOD_OPTIONS = [
  { value: 'this_month',    label: 'This Month' },
  { value: 'last_month',    label: 'Last Month' },
  { value: 'mtd',           label: 'MTD' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'ytd',           label: 'YTD' },
  { value: 'custom',        label: 'Custom Range' },
];

// Defensive fallback ONLY. The backend `/api/admin/marketing/performance-review`
// returns the canonical `kpi.label` (e.g. "Follower Growth"). Historically the
// delta helper's own `label` field spread into the KPI record and clobbered it;
// that collision has been fixed at source. This map remains solely as a safety
// net for missing/legacy payloads — it is NOT the primary heading source.
const KPI_HEADINGS = {
  follower_growth:     'Follower Growth',
  reach:               'Reach',
  impressions:         'Impressions',
  engagement:          'Engagement',
  engagement_rate:     'Engagement Rate',
  total_content:       'Content Published',
  actual_spend:        'Actual Marketing Spend',
  attributed_revenue:  'Attributed Revenue',
  cost_per_follower:   'Cost per Follower',
  cost_per_engagement: 'Cost per Engagement',
  mer:                 'MER',
};

const compactINR = (v) => {
  if (v == null || isNaN(v)) return '—';
  const n = Math.abs(Number(v));
  if (n >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};
const num = (v) => (v == null || isNaN(v)) ? '—' : Number(v).toLocaleString('en-IN');

// Metric-aware value formatter based on `unit` field returned by backend.
function fmtByUnit(v, unit) {
  if (v == null || isNaN(v)) return '—';
  if (unit === 'money') return compactINR(v);
  if (unit === 'pct') return `${Number(v).toFixed(1)}%`;
  if (unit === 'ratio') return `${Number(v).toFixed(2)}×`;
  return num(v);
}

// Verdict badge — respects metric-aware direction from backend.
// Neutral-flat fix: backend marks neutral-direction metrics (actual_spend,
// total_content) as verdict='flat' regardless of pct magnitude. Previously
// this rendered "No change" even when the value moved (e.g. +117%). We now
// render "No change" ONLY when pct is genuinely zero; otherwise show the
// actual signed percentage in a neutral tenant-tint chip.
function DeltaBadge({ pct, verdict, small = false }) {
  const isFlatWithMovement = verdict === 'flat' && pct != null && pct !== 0;
  const effectiveVerdict = isFlatWithMovement ? 'neutral' : verdict;

  const cls = effectiveVerdict === 'up'       ? 'bg-emerald-50 text-emerald-700'
            : effectiveVerdict === 'down'     ? 'bg-rose-50 text-rose-700'
            : effectiveVerdict === 'new_base' ? 'bg-amber-50 text-amber-700'
            : 'bg-[color:var(--color-primary-tint)] text-[color:var(--color-primary-hover)]';
  const Icon = effectiveVerdict === 'up'   ? TrendingUp
             : effectiveVerdict === 'down' ? TrendingDown
             : Minus;
  const label = effectiveVerdict === 'no_data'   ? 'No data'
              : effectiveVerdict === 'new_base'  ? 'New / no prior base'
              : effectiveVerdict === 'flat'      ? 'No change'
              : (pct != null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%` : '—');
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-semibold ${cls}
      ${small ? 'text-[10px] px-1.5 py-0.5' : 'text-[11px] px-2 py-0.5'}`}>
      <Icon className={small ? 'w-2.5 h-2.5' : 'w-3 h-3'} strokeWidth={2.5} />
      {label}
    </span>
  );
}

function KpiCard({ kpi, testId, comparisonLabel }) {
  // Card hierarchy (locked):
  //   [KPI LABEL]                 [↓ -X.X%]
  //   <current value>
  //   Jul 2026: <previous value>       (uses canonical backend period label)
  //
  // Canonical heading comes from `kpi.label` (backend). KPI_HEADINGS is only
  // a defensive fallback if the payload ever omits `label`.
  const heading = kpi.label || KPI_HEADINGS[kpi.key];
  return (
    <div className="surface-kpi p-4 flex flex-col gap-2" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <span className="kpi-label flex-shrink min-w-0" data-testid={`${testId}-label`}>
          {heading}
        </span>
        <DeltaBadge pct={kpi.pct} verdict={kpi.verdict} small />
      </div>
      <div className="text-[24px] font-bold text-[color:var(--color-text-primary)] tabular-nums leading-none"
        data-testid={`${testId}-value`}>
        {fmtByUnit(kpi.current, kpi.unit)}
      </div>
      <div className="text-[11px] text-[color:var(--color-text-muted)]" data-testid={`${testId}-previous`}>
        <span className="font-medium text-[color:var(--color-text-primary)]">{comparisonLabel || 'Previous period'}:</span>{' '}
        <span className="tabular-nums font-medium">{fmtByUnit(kpi.previous, kpi.unit)}</span>
      </div>
    </div>
  );
}

// Trend chart with metric selector — reach|impressions|engagement|followers
function PerformanceTrendChart({ trends, granularity }) {
  const [metric, setMetric] = useState('reach');
  const label = {
    reach: 'Reach', impressions: 'Impressions',
    engagement: 'Engagement', follower_growth: 'Follower Growth',
  }[metric];
  const rows = (trends || []).map(t => ({ label: t.label, value: t[metric] }));
  return (
    <div className="surface-kpi p-4">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <div className="kpi-label">Performance Trend</div>
          <div className="text-[12px] text-[color:var(--color-text-muted)] mt-1">
            {granularity === 'week' ? 'Weekly' : 'Monthly'} · {label}
          </div>
        </div>
        <div className="inline-flex bg-[color:var(--color-primary-tint)] rounded-md p-0.5" data-testid="mkt-trend-metric">
          {[
            { k: 'reach', l: 'Reach' },
            { k: 'impressions', l: 'Impressions' },
            { k: 'engagement', l: 'Engagement' },
            { k: 'follower_growth', l: 'Followers' },
          ].map(m => (
            <button key={m.k} onClick={() => setMetric(m.k)}
              className={`text-[10px] font-semibold px-2 py-1 rounded transition-colors
                ${metric === m.k
                  ? 'bg-white text-[color:var(--color-primary)] shadow-sm'
                  : 'text-[color:var(--color-primary-hover)] hover:text-[color:var(--color-primary)]'}`}>
              {m.l}
            </button>
          ))}
        </div>
      </div>
      <div className="h-56 print:h-48">
        {rows.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
              <CartesianGrid stroke="#EEF2EF" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: '#5C6B64', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#5C6B64', fontSize: 10 }} axisLine={false} tickLine={false} width={44} />
              <Tooltip formatter={(v) => num(v)} />
              <Line type="monotone" dataKey="value" name={label}
                stroke="var(--color-primary)" strokeWidth={2.5}
                dot={{ r: 3, fill: 'var(--color-primary)' }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-[12px] text-[#9CA3AF]">
            No data for this period
          </div>
        )}
      </div>
    </div>
  );
}

function SpendBudgetRevenueChart({ trends, budget }) {
  const rows = (trends || []).map(t => ({
    label: t.label,
    actual: t.actual_spend,
    revenue: t.attributed_revenue,
  }));
  const budgetPerPoint = trends?.length ? Math.round(budget / trends.length) : 0;
  return (
    <div className="surface-kpi p-4">
      <div className="kpi-label">Spend &amp; Efficiency</div>
      <div className="text-[12px] text-[color:var(--color-text-muted)] mt-1 mb-2">
        Actual Spend · Budget · Attributed Revenue
      </div>
      <div className="h-56 print:h-48">
        {rows.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
              <CartesianGrid stroke="#EEF2EF" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: '#5C6B64', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => compactINR(v).replace('₹','')}
                tick={{ fill: '#5C6B64', fontSize: 10 }} axisLine={false} tickLine={false} width={48} />
              <Tooltip formatter={(v) => compactINR(v)} />
              <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
              <Bar dataKey="actual" name="Actual Spend" fill="var(--color-primary)" maxBarSize={22} radius={[4,4,0,0]} />
              <Line type="monotone" dataKey="revenue" name="Attributed Revenue"
                stroke="#D4A017" strokeWidth={2.5} dot={{ r: 3 }} />
              {budgetPerPoint > 0 && (
                <Line type="monotone"
                  dataKey={() => budgetPerPoint} name="Budget (per point)"
                  stroke="#829C7A" strokeWidth={1.5} strokeDasharray="6 4" dot={false} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-[12px] text-[#9CA3AF]">
            No data for this period
          </div>
        )}
      </div>
    </div>
  );
}

function PlatformSpendChart({ platforms }) {
  const colors = ['var(--color-primary)', '#5B8C79', '#829C7A', '#B4B865', '#D4A017', '#C45A41', '#9A3D27', '#B8C4BD'];
  if (!platforms?.length) return (
    <div className="surface-kpi p-4">
      <div className="kpi-label">Spend by Platform</div>
      <div className="mt-6 text-[12px] text-[#9CA3AF] italic">No platform-tagged spend in this period</div>
    </div>
  );
  return (
    <div className="surface-kpi p-4">
      <div className="kpi-label">Spend by Platform</div>
      <div className="mt-3 space-y-2">
        {platforms.map((p, i) => (
          <div key={p.platform} className="flex items-center gap-3" data-testid={`mkt-platform-${p.platform}`}>
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: colors[i % colors.length] }} />
            <span className="text-[12px] text-[color:var(--color-text-primary)] font-medium min-w-0 flex-1 truncate">{p.platform}</span>
            <span className="text-[12px] tabular-nums font-semibold text-[color:var(--color-text-primary)]">{compactINR(p.actual_spend)}</span>
            <span className="text-[11px] text-[color:var(--color-text-muted)] tabular-nums w-12 text-right">{p.share_pct}%</span>
          </div>
        ))}
      </div>
      <div className="mt-4 h-2 flex rounded-full overflow-hidden bg-[#EEF2EF]">
        {platforms.map((p, i) => (
          <div key={p.platform} style={{ width: `${p.share_pct}%`, background: colors[i % colors.length] }} />
        ))}
      </div>
    </div>
  );
}

function ContentProductivity({ content }) {
  const rows = [
    { label: 'Posts',   value: content.posts },
    { label: 'Reels',   value: content.reels },
    { label: 'Stories', value: content.stories },
    { label: 'Videos',  value: content.videos },
  ];
  return (
    <div className="surface-kpi p-4">
      <div className="kpi-label">Content Productivity</div>
      <div className="grid grid-cols-4 gap-2 mt-3">
        {rows.map(r => (
          <div key={r.label} className="text-center bg-[#FAFCFA] rounded-md p-2 border border-[#EEF2EF]">
            <div className="text-[9px] uppercase tracking-wider text-[color:var(--color-text-muted)]">{r.label}</div>
            <div className="text-[18px] font-bold text-[color:var(--color-text-primary)] tabular-nums mt-1">{num(r.value)}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        <div className="flex items-center justify-between">
          <span className="text-[color:var(--color-text-muted)]">Total Content</span>
          <span className="font-semibold tabular-nums text-[color:var(--color-text-primary)]">{num(content.total)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[color:var(--color-text-muted)]">Reach / Piece</span>
          <span className="font-semibold tabular-nums text-[color:var(--color-text-primary)]">{num(content.reach_per_content)}</span>
        </div>
        <div className="flex items-center justify-between col-span-2">
          <span className="text-[color:var(--color-text-muted)]">Engagement / Piece</span>
          <span className="font-semibold tabular-nums text-[color:var(--color-text-primary)]">{num(content.engagement_per_content)}</span>
        </div>
      </div>
    </div>
  );
}

function ActivityCommitment({ activity }) {
  return (
    <div className="surface-kpi p-4">
      <div className="kpi-label">Actual vs Planned Activity</div>
      <div className="mt-3 space-y-2">
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-[color:var(--color-text-muted)]">Actual Spend</span>
          <span className="font-bold tabular-nums text-[color:var(--color-text-primary)]" data-testid="mkt-activity-actual">
            {compactINR(activity.actual_spend)}
          </span>
        </div>
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-[color:var(--color-text-muted)]">Planned Future</span>
          <span className="font-semibold tabular-nums text-amber-700" data-testid="mkt-activity-planned">
            {compactINR(activity.planned_spend)}
          </span>
        </div>
        <div className="pt-2 border-t border-[#EEF2EF] flex items-center justify-between text-[13px]">
          <span className="text-[color:var(--color-text-primary)] font-semibold">Total Committed</span>
          <span className="font-bold tabular-nums text-[color:var(--color-text-primary)]" data-testid="mkt-activity-committed">
            {compactINR(activity.total_committed)}
          </span>
        </div>
        <p className="text-[10px] text-[color:var(--color-text-muted)] italic mt-1">
          Planned amounts stay excluded from Actual performance calculations.
        </p>
      </div>
    </div>
  );
}

function ReportingDiscipline({ discipline }) {
  const pct = discipline.pct;
  const iconCls = pct >= 100 ? 'text-emerald-700'
                : pct >= 75  ? 'text-amber-700'
                : 'text-rose-700';
  const Icon = pct >= 100 ? CheckCircle2 : AlertTriangle;
  return (
    <div className="surface-kpi p-4">
      <div className="kpi-label">Reporting Discipline</div>
      <div className="mt-3 flex items-center gap-3">
        <Icon className={`w-8 h-8 ${iconCls}`} strokeWidth={2} />
        <div>
          <div className="text-[22px] font-bold text-[color:var(--color-text-primary)] tabular-nums leading-none">
            {discipline.submitted_weeks} / {discipline.expected_weeks}
          </div>
          <div className={`text-[11px] font-semibold mt-1 ${iconCls}`}>
            {pct.toFixed(1)}% submitted
          </div>
        </div>
      </div>
      {discipline.missing_weeks?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#EEF2EF]">
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--color-text-muted)]">Missing weeks</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {discipline.missing_weeks.slice(0, 6).map((w, i) => (
              <span key={i} className="text-[10px] bg-rose-50 text-rose-700 rounded px-1.5 py-0.5 font-mono">
                {w.week_start.slice(5)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PerformanceSignals({ kpis, budget, discipline }) {
  const pick = (k) => kpis.find(x => x.key === k) || {};
  const rows = [
    { l: 'Reach',           k: pick('reach') },
    { l: 'Engagement',      k: pick('engagement') },
    { l: 'Followers',       k: pick('follower_growth') },
    { l: 'Cost/Follower',   k: pick('cost_per_follower') },
    { l: 'Cost/Engagement', k: pick('cost_per_engagement') },
    { l: 'Content Output',  k: pick('total_content') },
  ];
  const spendVsBudget = budget.variance_pct;
  return (
    <div className="surface-kpi p-4">
      <div className="kpi-label">Performance Signals</div>
      <div className="mt-3 space-y-1.5">
        {rows.map(r => (
          <div key={r.l} className="flex items-center justify-between text-[12px]">
            <span className="text-[color:var(--color-text-muted)]">{r.l}</span>
            <DeltaBadge pct={r.k.pct} verdict={r.k.verdict} small />
          </div>
        ))}
        <div className="flex items-center justify-between text-[12px] pt-1.5 border-t border-[#EEF2EF]">
          <span className="text-[color:var(--color-text-muted)]">Spend vs Budget</span>
          <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${
            budget.status === 'under' ? 'bg-emerald-50 text-emerald-700'
            : budget.status === 'over' ? 'bg-rose-50 text-rose-700'
            : budget.status === 'on'   ? 'bg-[color:var(--color-primary-tint)] text-[color:var(--color-primary-hover)]'
            : 'bg-[#F3F5F2] text-[#5C6B64]'}`}>
            {budget.status === 'under' ? `${spendVsBudget?.toFixed(1)}% under`
              : budget.status === 'over' ? `${spendVsBudget?.toFixed(1)}% over`
              : budget.status === 'on'   ? 'On budget'
              : 'No budget set'}
          </span>
        </div>
        <div className="flex items-center justify-between text-[12px]">
          <span className="text-[color:var(--color-text-muted)]">Reporting</span>
          <span className="text-[11px] font-semibold tabular-nums text-[color:var(--color-text-primary)]">
            {discipline.submitted_weeks}/{discipline.expected_weeks} weeks
          </span>
        </div>
      </div>
    </div>
  );
}

function OutletComparison({ outlets }) {
  if (!outlets?.length || outlets.length <= 1) return null;
  return (
    <div className="surface-kpi p-4 print:break-inside-avoid">
      <div className="kpi-label">Outlet Comparison</div>
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-[11px] tabular-nums">
          <thead>
            <tr className="text-left text-[color:var(--color-text-muted)] uppercase tracking-wider text-[9px]">
              <th className="pb-2 pr-3">Outlet</th>
              <th className="pb-2 pr-3 text-right">Reach</th>
              <th className="pb-2 pr-3 text-right">Δ</th>
              <th className="pb-2 pr-3 text-right">Engagement</th>
              <th className="pb-2 pr-3 text-right">Followers</th>
              <th className="pb-2 pr-3 text-right">Spend</th>
              <th className="pb-2 pr-3 text-right">₹/Follower</th>
              <th className="pb-2 pr-3 text-right">₹/Engagement</th>
              <th className="pb-2 text-right">Reporting</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EEF2EF]">
            {outlets.map((o) => (
              <tr key={o.outlet_id} data-testid={`mkt-outlet-row-${o.outlet_id}`}>
                <td className="py-2 pr-3 font-medium text-[color:var(--color-text-primary)]">{o.outlet_name}</td>
                <td className="py-2 pr-3 text-right">{num(o.reach)}</td>
                <td className="py-2 pr-3 text-right"><DeltaBadge pct={o.reach_delta?.pct} verdict={o.reach_delta?.verdict} small /></td>
                <td className="py-2 pr-3 text-right">{num(o.engagement)}</td>
                <td className="py-2 pr-3 text-right">{num(o.follower_growth)}</td>
                <td className="py-2 pr-3 text-right">{compactINR(o.actual_spend)}</td>
                <td className="py-2 pr-3 text-right">{compactINR(o.cost_per_follower)}</td>
                <td className="py-2 pr-3 text-right">{compactINR(o.cost_per_engagement)}</td>
                <td className="py-2 text-right">
                  <span className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold
                    ${o.reporting_pct >= 100 ? 'bg-emerald-50 text-emerald-700'
                      : o.reporting_pct >= 75 ? 'bg-amber-50 text-amber-700'
                      : 'bg-rose-50 text-rose-700'}`}>
                    {o.reporting_pct.toFixed(0)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ITER-marketing-pass2-loading — premium skeleton primitives for the
// initial-load state. Filter-change updates reuse the last-known dashboard
// with a subtle veil + "Updating review…" chip (see body of component).
function Skel({ className = '', style }) {
  return (
    <div
      className={`bg-[color:var(--color-primary-tint)] animate-pulse rounded ${className}`}
      style={style}
    />
  );
}
function SkelKpiCard({ testId }) {
  return (
    <div className="surface-kpi p-4 flex flex-col gap-2" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <Skel className="h-4 w-24" />
        <Skel className="h-4 w-14 rounded-full" />
      </div>
      <Skel className="h-7 w-28 mt-1" />
      <Skel className="h-3 w-32" />
    </div>
  );
}
function SkelChartCard({ heightCls = 'h-56', testId }) {
  return (
    <div className="surface-kpi p-4" data-testid={testId}>
      <Skel className="h-4 w-40 mb-1.5" />
      <Skel className="h-3 w-28 mb-4" />
      <div className={`${heightCls} flex items-end gap-1.5`}>
        {[62, 78, 45, 82, 68, 92, 55, 74, 88, 60, 70, 84].map((h, i) => (
          <Skel key={i} className="flex-1 rounded-t" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
}
function SkelListCard({ rows = 4, testId }) {
  return (
    <div className="surface-kpi p-4" data-testid={testId}>
      <Skel className="h-4 w-40 mb-3" />
      <div className="space-y-2">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center justify-between gap-3">
            <Skel className="h-3 w-24" />
            <Skel className="h-3 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
function SkelTableCard({ testId }) {
  return (
    <div className="surface-kpi p-4" data-testid={testId}>
      <Skel className="h-4 w-40 mb-3" />
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skel className="h-4 w-32" />
            <Skel className="h-4 flex-1" />
            <Skel className="h-4 w-14" />
            <Skel className="h-4 w-14" />
            <Skel className="h-4 w-14" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MarketingPerformanceReview({
  portfolioId = '', outletId = '', outletName = '', portfolioName = '', tenantName = '',
}) {
  const now = new Date();
  const [period, setPeriod] = useState('this_month');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const getHeaders = useCallback(() => {
    const tok = localStorage.getItem('token');
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true); setErr(null);
      try {
        const params = new URLSearchParams({ period, year, month });
        if (portfolioId) params.append('portfolio_id', portfolioId);
        if (outletId) params.append('outlet_ids', outletId);
        if (period === 'custom') {
          if (!customStart || !customEnd) { setLoading(false); return; }
          params.append('start_date', customStart);
          params.append('end_date', customEnd);
        }
        const res = await axios.get(
          `${API}/api/admin/marketing/performance-review?${params.toString()}`,
          { headers: getHeaders() }
        );
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message);
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [period, year, month, customStart, customEnd, portfolioId, outletId, getHeaders]);

  const handlePrint = () => {
    if (!data || loading) { toast.error('Nothing to print — load a review first'); return; }
    window.print();
  };

  // Guards:
  //   showSkeleton  → true when we have NO previous data to reuse.
  //   isUpdating    → true when we DO have prior data and a new fetch is
  //                   in flight (filter/period change). Preserves the
  //                   existing dashboard behind a subtle veil so the page
  //                   never collapses into a blank screen.
  const showSkeleton = loading && !data;
  const isUpdating = loading && !!data;

  const isMonthly = period === 'this_month' || period === 'last_month';
  const printTitle = isMonthly ? 'Monthly Performance Review' : 'Marketing Performance Review';
  const scopeLabel = outletName || (portfolioName ? `${portfolioName} — All Outlets` : 'All Outlets');

  return (
    <div className="marketing-perf-review" data-testid="mkt-perf-review">
      {/* Filters row — hidden in print, ALWAYS visible on screen */}
      <div className="no-print flex flex-wrap items-center gap-2 mb-4">
        <Select value={period} onValueChange={setPeriod} disabled={showSkeleton}>
          <SelectTrigger className="w-44 h-9 border-[#E2E8DF] text-sm" data-testid="mkt-perf-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(period === 'this_month' || period === 'last_month' || period === 'mtd') && (
          <>
            <Select value={String(month)} onValueChange={v => setMonth(parseInt(v))} disabled={showSkeleton}>
              <SelectTrigger className="w-24 h-9 border-[#E2E8DF] text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i) => (
                  <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={v => setYear(parseInt(v))} disabled={showSkeleton}>
              <SelectTrigger className="w-24 h-9 border-[#E2E8DF] text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024,2025,2026,2027].map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
        {period === 'custom' && (
          <>
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              className="h-9 rounded-md border border-[#E2E8DF] px-2 text-sm" data-testid="mkt-perf-start" />
            <span className="text-[color:var(--color-text-muted)] text-sm">→</span>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              className="h-9 rounded-md border border-[#E2E8DF] px-2 text-sm" data-testid="mkt-perf-end" />
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          {isUpdating && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full bg-[color:var(--color-primary-tint)] text-[color:var(--color-primary-hover)]"
              data-testid="mkt-perf-updating">
              <span className="w-2 h-2 rounded-full bg-[color:var(--color-primary)] animate-pulse" />
              Updating review…
            </span>
          )}
          <Button onClick={handlePrint} disabled={loading || !data}
            className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="mkt-perf-print">
            <Printer className="w-4 h-4" /> Print Performance Review
          </Button>
        </div>
      </div>

      {/* Compact error panel — filters stay available for retry */}
      {err && (
        <div className="surface-kpi p-4 mb-4 flex items-start gap-3 border-l-4 border-l-rose-500"
          data-testid="mkt-perf-error">
          <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" strokeWidth={2} />
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-[color:var(--color-text-primary)]">Could not load Performance Review</div>
            <div className="text-[12px] text-[color:var(--color-text-muted)] mt-0.5">{err}</div>
            <div className="text-[11px] text-[color:var(--color-text-muted)] mt-1 italic">Adjust filters or period to retry.</div>
          </div>
        </div>
      )}

      {/* Initial-load skeleton — mirrors the final composition so there's
          no layout jump when data arrives. */}
      {showSkeleton && !err && (
        <div data-testid="mkt-perf-skeleton">
          <div className="mb-4 flex items-center gap-3">
            <Skel className="h-4 w-24" />
            <Skel className="h-4 w-64" />
            <Skel className="h-4 w-56" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <SkelKpiCard key={i} testId={`mkt-perf-skeleton-kpi-${i}`} />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
            <SkelChartCard testId="mkt-perf-skeleton-trend" />
            <SkelChartCard testId="mkt-perf-skeleton-spend" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            <SkelListCard rows={4} testId="mkt-perf-skeleton-platform" />
            <SkelListCard rows={4} testId="mkt-perf-skeleton-content" />
            <SkelListCard rows={3} testId="mkt-perf-skeleton-activity" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <SkelListCard rows={6} testId="mkt-perf-skeleton-signals" />
            <SkelListCard rows={2} testId="mkt-perf-skeleton-discipline" />
          </div>
          <SkelTableCard testId="mkt-perf-skeleton-outlets" />
        </div>
      )}

      {/* Real content — retained when re-fetching so period changes never
          collapse the page. Slight veil + pointer-events lock during update. */}
      {data && (
        <div className={isUpdating ? 'opacity-60 pointer-events-none transition-opacity duration-200' : 'transition-opacity duration-200'}
          data-testid="mkt-perf-body">
          {/* Print-only header */}
          <div className="hidden print:block print:mb-4">
        <div className="border-b-2 border-[color:var(--color-primary)] pb-3 mb-3">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--color-text-muted)]">RestroPulse</div>
          <h1 className="text-2xl font-bold text-[color:var(--color-text-primary)]" data-testid="mkt-perf-print-title">
            {printTitle}
          </h1>
        </div>
        <div className="grid grid-cols-2 gap-2 text-[11px] text-[color:var(--color-text-primary)]">
          <div><strong>Tenant:</strong> {tenantName || 'RestroPulse'}</div>
          <div><strong>Scope:</strong> {scopeLabel}</div>
          <div><strong>Review Period:</strong> {data.period.current.label} ({data.period.current.start} → {data.period.current.end})</div>
          <div><strong>Comparison:</strong> {data.period.comparison.label} ({data.period.comparison.start} → {data.period.comparison.end})</div>
          <div className="col-span-2 text-[color:var(--color-text-muted)]">
            <strong>Generated:</strong> {format(new Date(data.generated_at), 'dd MMM yyyy, HH:mm')}
          </div>
        </div>
      </div>

      {/* Screen header — period context */}
      <div className="no-print mb-4 flex items-center gap-3">
        <div className="kpi-label">Review Period</div>
        <div className="text-[12px] text-[color:var(--color-text-primary)]">
          <strong>{data.period.current.label}</strong> <span className="text-[color:var(--color-text-muted)]">({data.period.current.start} → {data.period.current.end})</span>
        </div>
        <div className="text-[12px] text-[color:var(--color-text-muted)]">
          vs <strong className="text-[color:var(--color-text-primary)]">{data.period.comparison.label}</strong> ({data.period.comparison.start} → {data.period.comparison.end})
        </div>
      </div>

      {/* Executive Pulse — KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {data.kpis.map(kpi => (
          <KpiCard key={kpi.key} kpi={kpi} testId={`mkt-perf-kpi-${kpi.key}`}
            comparisonLabel={data.period.comparison.label} />
        ))}
      </div>

      {/* Two-column trend row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        <PerformanceTrendChart trends={data.trends} granularity={data.period.granularity} />
        <SpendBudgetRevenueChart trends={data.trends} budget={data.budget.current} />
      </div>

      {/* Three-column secondary row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        <PlatformSpendChart platforms={data.platform_spend} />
        <ContentProductivity content={data.content} />
        <ActivityCommitment activity={data.activity} />
      </div>

      {/* Signals row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <PerformanceSignals kpis={data.kpis} budget={data.budget} discipline={data.discipline} />
        <ReportingDiscipline discipline={data.discipline} />
      </div>

      {/* Outlet comparison — portfolio scope */}
      <OutletComparison outlets={data.outlets} />
        </div>
      )}
    </div>
  );
}
